// src/pages/ProjectedBalanceSheet.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function ProjectedBalanceSheet() {
  const { projectId } = useParams();

  // ─── State Hooks ─────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rawInputs, setRawInputs] = useState(null);

  // API-driven data:
  const [projectYears, setProjectYears] = useState([]);
  const [capacityUtilization, setCapacityUtilization] = useState([]);
  const [netProfitAfterTax, setNetProfitAfterTax] = useState([]);
  const [financeTotals, setFinanceTotals] = useState({});
  const [termLoanInput, setTermLoanInput] = useState({});
  const [emiScheduleLocal, setEmiScheduleLocal] = useState([]);
  const [balanceSheetInputs, setBalanceSheetInputs] = useState({});
  const [otherCurrentAssetsByYear, setOtherCurrentAssetsByYear] = useState([]);
  const [projectedProfitabilityInputs, setProjectedProfitabilityInputs] =
    useState({});
  const [totalAnnualSales, setTotalAnnualSales] = useState([]);
  const [workingCapitalRequirementRaw, setWorkingCapitalRequirementRaw] =
    useState([]);
  const [productWiseRawMaterialCostRs, setProductWiseRawMaterialCostRs] =
    useState({});
  const [manufacturedProducts, setManufacturedProducts] = useState([]);
  const [fgValueByYear, setFgValueByYear] = useState([]);
  const [sundryDebtorsByYear, setSundryDebtorsByYear] = useState([]);

  // LocalStorage-driven data:
  const [workingCapitalBank, setWorkingCapitalBank] = useState([]);
  const [sundryCreditors, setSundryCreditors] = useState([]);

  // Derived:
  const [proprietorAccount, setProprietorAccount] = useState([]);
  const [termLoanBalance, setTermLoanBalance] = useState([]);
  const [depositsB, setDepositsB] = useState(0);
  const [cumulativeDep, setCumulativeDep] = useState([]);
  const [sundryDebtorsLocal, setSundryDebtorsLocal] = useState([]);
  const [loanAndAdvances, setLoanAndAdvances] = useState([]);

  const [rawMaterialsByYear, setRawMaterialsByYear] = useState([]);
  const [finishedGoodsByYear, setFinishedGoodsByYear] = useState([]);

  // ✅ Mismatch UI
  const [showMismatchDialog, setShowMismatchDialog] = useState(false);
  const [dismissedMismatchOnce, setDismissedMismatchOnce] = useState(false);

  // ─── Helper: generate month labels for EMI schedule ───────────────────────
  const generateMonthLabels = (startStr, length) => {
    if (!startStr || !startStr.includes("-")) return [];
    const [startM, startY] = startStr.split("-").map(Number);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    let m = startM - 1,
      y = startY;
    return Array.from({ length }, () => {
      const label = `${months[m]}-${y}`;
      m = (m + 1) % 12;
      if (m === 0) y++;
      return label;
    });
  };

  // ─── Helper: bucket EMI schedule into FY-wise summary ────────────────────
  const bucketEmiSchedule = useCallback((emiSchedule, startMonthStr) => {
    const monthLabels = generateMonthLabels(startMonthStr, emiSchedule.length);
    const monthsMap = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };
    const bucket = {};
    emiSchedule.forEach((row, idx) => {
      const lbl = monthLabels[idx] || "";
      const [monStr, yrStr] = lbl.split("-");
      const mon = monthsMap[monStr] || 0;
      const yr = parseInt(yrStr, 10) || 0;
      const fyStart = mon >= 4 ? yr : yr - 1;
      const fyLabel = `${fyStart}-${String(fyStart + 1).slice(-2)}`;
      if (!bucket[fyLabel]) {
        bucket[fyLabel] = {
          year: fyLabel,
          totalPrincipal: 0,
          totalInterest: 0,
          closingBalance: 0,
        };
      }
      bucket[fyLabel].totalPrincipal += row.principal;
      bucket[fyLabel].totalInterest += row.interest;
      bucket[fyLabel].closingBalance = row.closingBalance;
    });
    return Object.values(bucket);
  }, []);

  // ─── Fetch API & localStorage on mount ─────────────────────────────────────
  useEffect(() => {
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setRawInputs(data);

        // a) projectYears & capacity utilization
        setProjectYears(
          Array.isArray(data.projectYears) ? data.projectYears : []
        );
        setCapacityUtilization(data.capacityUtilizationPerYear || []);
        setWorkingCapitalBank(data.workingCapitalBank || []);
        setSundryCreditors(data.sundryCreditor || []);

        // b) netProfitAfterTax & financeTotals
        setNetProfitAfterTax(
          Array.isArray(data.netProfitAfterTax)
            ? data.netProfitAfterTax.map((v) => parseFloat(v) || 0)
            : []
        );
        setFinanceTotals(data.financeTotals || {});

        // c) termLoanInput & EMI schedule (from API or localStorage)
        const term = data.termLoanInput || {};
        setTermLoanInput(term);
        let emiSchedule = Array.isArray(term.emiSchedule)
          ? term.emiSchedule
          : [];
        if (!emiSchedule.length) {
          const localEmi = JSON.parse(
            localStorage.getItem("emiSchedule") || "[]"
          );
          if (Array.isArray(localEmi) && localEmi.length) {
            emiSchedule = localEmi;
          }
        }
        setEmiScheduleLocal(emiSchedule);

        // d) balanceSheetInputs
        setBalanceSheetInputs(data.balanceSheetInputs || {});

        // e) otherCurrentAssetsByYear
        setOtherCurrentAssetsByYear(
          Array.isArray(data.otherCurrentAssetsByYear)
            ? data.otherCurrentAssetsByYear.map((v) => parseFloat(v) || 0)
            : []
        );

        setRawMaterialsByYear(
          Array.isArray(data.rawMaterials)
            ? data.rawMaterials.map((v) => parseFloat(v) || 0)
            : []
        );
        setFinishedGoodsByYear(
          Array.isArray(data.finishedGoods)
            ? data.finishedGoods.map((v) => parseFloat(v) || 0)
            : []
        );

        // f) preliminary expenses & projections
        setProjectedProfitabilityInputs(
          data.projectedProfitabilityInputs || {}
        );

        // g) totalAnnualSales & workingCapitalRequirement raw
        setTotalAnnualSales(
          Array.isArray(data.totalAnnualSales)
            ? data.totalAnnualSales.map((v) => parseFloat(v) || 0)
            : []
        );
        setWorkingCapitalRequirementRaw(
          data.workingCapitalRequirementData || []
        );

        // h) product-wise RM cost & products
        setProductWiseRawMaterialCostRs(
          data.productWiseRawMaterialCostRs || {}
        );
        setManufacturedProducts(data.manufacturedProducts || []);

        // i) FG values
        setFgValueByYear(
          Array.isArray(data.fgValueByYear)
            ? data.fgValueByYear.map((v) => parseFloat(v) || 0)
            : []
        );

        // j) sundryDebtorsByYear (from DB)
        setSundryDebtorsByYear(
          Array.isArray(data.sundryDebtors)
            ? data.sundryDebtors.map((v) => parseFloat(v) || 0)
            : []
        );
      })
      .catch((err) => {
        console.error("Failed to fetch balance-sheet data:", err);
        setError("Unable to load projected balance sheet data.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectId]);

  // ─── Parse “mm-yyyy” → { month, year } ───────────────────────────────────
  const prod = useMemo(() => {
    const [m, y] = (termLoanInput.startMonth || "").split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  // ─── Months active in FY “YYYY-YY” ──────────────────────────────────────
  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prod) return 12;
      const fyStart = parseInt(fyLabel.slice(0, 4), 10);
      if (isNaN(fyStart)) return 12;
      const fyEnd = fyStart + 1;
      const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prod]
  );

  // ─── Full-year declining‐balance dep’n for yearIndex ─────────────────────
  const calcFullYear = (cost, rate, yearIndex) => {
    if (yearIndex === 0) return 0;
    let remaining = cost,
      dep = 0;
    for (let i = 1; i <= yearIndex; i++) {
      dep = (remaining * rate) / 100;
      remaining -= dep;
    }
    return dep;
  };

  // ─── Cost lookup from rawInputs (same as Depreciation.jsx) ───────────────
  const getCostByParticular = useCallback(
    (p) => {
      if (!rawInputs) return 0;

      const {
        costProjectInputs = {},
        plantMachineryData = [],
        plantContingencies,
        plantGstPercent,
        furnitureAndFixtures = [],
        otherFixedAssets = [],
        computers = [],
        preliminaryExpenses = [],
      } = rawInputs;

      const keyMap = {
        "Land & Site Development (On Lease)": "landDevelopment",
        "Civil Construction (On Lease)": "civilConstruction",
      };
      if (keyMap[p] && costProjectInputs[keyMap[p]] != null) {
        return parseFloat(costProjectInputs[keyMap[p]]) || 0;
      }

      switch (p) {
        case "Plant & Machinery": {
          const subtotal = plantMachineryData.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
          const cont = (subtotal * (parseFloat(plantContingencies) || 0)) / 100;
          const tax =
            ((subtotal + cont) * (parseFloat(plantGstPercent) || 0)) / 100;
          return subtotal + cont + tax;
        }
        case "Furniture and Fixture":
          return furnitureAndFixtures.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );

        case "Miscellaneous Fixed Assets":
          return otherFixedAssets.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );

        case "Computer and Software":
          return computers.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

        case "Preliminary Expenses":
          return preliminaryExpenses.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );

        default:
          return 0;
      }
    },
    [rawInputs]
  );

  // ─── Depreciation schedule & costTotal (from Depreciation.jsx logic) ────
  const { costTotal: depreciationCostTotal, yearly: depreciationTotals } =
    useMemo(() => {
      if (!rawInputs) return { costTotal: 0, yearly: [] };

      const rows =
        Array.isArray(rawInputs.depreciationData) &&
        rawInputs.depreciationData.length
          ? rawInputs.depreciationData
          : [];

      const costs = rows.map((r) => getCostByParticular(r.particular));
      const costTotal = costs.reduce((s, c) => s + c, 0);

      const yearly = projectYears.map((fy, yi) => {
        return rows.reduce((sum, r) => {
          const cost = getCostByParticular(r.particular);
          const full = calcFullYear(cost, r.taxPercent, yi);
          const months = getMonthsActive(fy);
          return sum + full * (months / 12);
        }, 0);
      });

      return {
        costTotal: +costTotal.toFixed(2),
        yearly: yearly.map((v) => +v.toFixed(2)),
      };
    }, [rawInputs, projectYears, getCostByParticular, getMonthsActive]);

  // ─── Compute Term Loan Balance per FY ─────────────────────────────────────
  useEffect(() => {
    const balances = projectYears.map(() => 0);

    const rawStart =
      termLoanInput.pStartMonth && termLoanInput.pStartMonth.includes("-")
        ? termLoanInput.pStartMonth
        : termLoanInput.startMonth || "";

    if (emiScheduleLocal.length && rawStart) {
      const summary = bucketEmiSchedule(emiScheduleLocal, rawStart);
      projectYears.forEach((yearLabel, idx) => {
        const found = summary.find((s) => s.year === yearLabel);
        balances[idx] = found ? parseFloat(found.closingBalance) || 0 : 0;
      });
    }

    setTermLoanBalance(balances);
  }, [
    projectYears,
    emiScheduleLocal,
    termLoanInput.pStartMonth,
    termLoanInput.startMonth,
    bucketEmiSchedule,
  ]);

  // ─── Proprietor’s Capital Account using CostOfTheProject logic ───────────
  useEffect(() => {
    if (!projectYears.length || !netProfitAfterTax.length || !rawInputs) return;

    const wcrArr = Array.isArray(rawInputs.workingCapitalRequirementData)
      ? rawInputs.workingCapitalRequirementData
      : [];
    const wcrFirstYear = wcrArr.find((v) => v > 0) || 0;

    const pmSubtotal = (rawInputs.plantMachineryData || []).reduce(
      (sum, row) => sum + (Number(row.amount) || 0),
      0
    );
    const pmContAmt =
      (pmSubtotal * (Number(rawInputs.plantContingencies) || 0)) / 100;
    const pmTaxAmt =
      ((pmSubtotal + pmContAmt) * (Number(rawInputs.plantGstPercent) || 0)) /
      100;
    const plantMachineryGrandTotal = pmSubtotal + pmContAmt + pmTaxAmt;

    const furnitureTotal = (rawInputs.furnitureAndFixtures || []).reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0
    );
    const miscFixedAssetsTotal = (rawInputs.otherFixedAssets || []).reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0
    );
    const computerSoftwareTotal = (rawInputs.computers || []).reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0
    );
    const preliminaryExpensesTotal = (
      rawInputs.preliminaryExpenses || []
    ).reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

    const landDev =
      parseFloat(rawInputs.costProjectInputs?.landDevelopment) || 0;
    const civilConst =
      parseFloat(rawInputs.costProjectInputs?.civilConstruction) || 0;
    const deposits = parseFloat(rawInputs.costProjectInputs?.deposits) || 0;
    const wcPct =
      parseFloat(rawInputs.costProjectInputs?.workingCapitalPct) || 0;
    const termLoanPct =
      parseFloat(rawInputs.costProjectInputs?.termLoanPct) || 0;

    const projectCost =
      landDev +
      civilConst +
      plantMachineryGrandTotal +
      furnitureTotal +
      miscFixedAssetsTotal +
      computerSoftwareTotal +
      preliminaryExpensesTotal +
      wcrFirstYear +
      deposits;

    const workingCapitalTotal = Math.round((wcrFirstYear * wcPct) / 100);
    const termLoanTotal = Math.round(
      (projectCost - wcrFirstYear) * (termLoanPct / 100)
    );
    const equityTotal = projectCost - termLoanTotal - workingCapitalTotal;

    const propArr = [];
    projectYears.forEach((_, i) => {
      const drawing = parseFloat(rawInputs.promoterDrawings?.[i]) || 0;
      if (i === 0) {
        propArr[i] = equityTotal + (netProfitAfterTax[i] || 0);
      } else {
        propArr[i] = propArr[i - 1] + (netProfitAfterTax[i] || 0) - drawing;
      }
    });
    setProprietorAccount(propArr);
  }, [projectYears, netProfitAfterTax, rawInputs]);

  // ─── 6) Deposits (B) ─────────────────────────────────────────────────────
  useEffect(() => {
    setDepositsB(parseFloat(balanceSheetInputs.depositsB) || 0);
  }, [balanceSheetInputs]);

  // ─── Compute prelimTotal and write-off ──────────────────────────────────
  const prelimTotal = useMemo(() => {
    if (!rawInputs) return 0;
    return (rawInputs.preliminaryExpenses || []).reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0
    );
  }, [rawInputs]);

  const prelimWrittenOff =
    parseFloat(projectedProfitabilityInputs.preliminaryExpensesWrittenOff) || 0;

  // ─── 8) Cumulative Depreciation ─────────────────────────────────────────
  useEffect(() => {
    const cum = [];
    depreciationTotals.forEach((val, i) => {
      cum[i] = i === 0 ? val : cum[i - 1] + val;
    });
    setCumulativeDep(cum);
  }, [depreciationTotals]);

  // ─── 10) Loans & Advances ───────────────────────────────────────────────
  useEffect(() => {
    if (!projectYears.length) return;
    const principal = parseFloat(balanceSheetInputs.loanAndAdvances) || 0;
    const pct = parseFloat(balanceSheetInputs.loanAndAdvancesPct) || 0;
    const loans = [];
    projectYears.forEach((_, i) => {
      loans[i] = i === 0 ? principal : (loans[i - 1] * pct) / 100;
    });
    setLoanAndAdvances(loans);
  }, [projectYears, balanceSheetInputs]);

  // ─── 12) Sundry Debtors (from DB) ────────────────────────────────────────
  useEffect(() => {
    setSundryDebtorsLocal(sundryDebtorsByYear);
  }, [sundryDebtorsByYear]);

  // ─── Main Liabilities & Equity totals ────────────────────────────────────
  const mainTotals = useMemo(
    () =>
      projectYears.map(
        (_, i) =>
          (proprietorAccount[i] || 0) +
          (termLoanBalance[i] || 0) +
          (workingCapitalBank[i] || 0) +
          (sundryCreditors[i] || 0) +
          depositsB
      ),
    [
      projectYears,
      proprietorAccount,
      termLoanBalance,
      workingCapitalBank,
      sundryCreditors,
      depositsB,
    ]
  );

  // ─── Net Block ───────────────────────────────────────────────────────────
  const netBlock = useMemo(
    () =>
      projectYears.map(
        (_, i) => depreciationCostTotal - (cumulativeDep[i] || 0)
      ),
    [projectYears, depreciationCostTotal, cumulativeDep]
  );

  // ─── Amortised Expenses ──────────────────────────────────────────────────
  const amortisedExpenses = useMemo(() => {
    const arr = [];
    projectYears.forEach((_, i) => {
      if (i === 0) arr[i] = prelimTotal;
      else arr[i] = Math.max((arr[i - 1] || 0) - prelimWrittenOff, 0);
    });
    return arr;
  }, [projectYears, prelimTotal, prelimWrittenOff]);

  // ─── Third Totals ────────────────────────────────────────────────────────
  const thirdTotals = useMemo(
    () =>
      projectYears.map((_, i) =>
        [
          amortisedExpenses[i] || 0,
          depositsB,
          sundryDebtorsLocal[i] || 0,
          otherCurrentAssetsByYear[i] || 0,
          loanAndAdvances[i] || 0,
        ].reduce((sum, v) => sum + v, 0)
      ),
    [
      projectYears,
      amortisedExpenses,
      depositsB,
      sundryDebtorsLocal,
      otherCurrentAssetsByYear,
      loanAndAdvances,
    ]
  );

  // ─── Cash & Bank Balance ─────────────────────────────────────────────────
  const cashAndBankBalance = useMemo(
    () =>
      projectYears.map((_, i) => {
        const total = mainTotals[i] || 0;
        const nb = netBlock[i] || 0;
        return Number((total - nb - thirdTotals[i]).toFixed(2));
      }),
    [projectYears, mainTotals, netBlock, thirdTotals]
  );

  // ─── Fourth Totals ───────────────────────────────────────────────────────
  const fourthTotals = useMemo(
    () =>
      projectYears.map((_, i) =>
        [
          amortisedExpenses[i] || 0,
          depositsB,
          sundryDebtorsLocal[i] || 0,
          0,
          otherCurrentAssetsByYear[i] || 0,
          loanAndAdvances[i] || 0,
          cashAndBankBalance[i] || 0,
        ].reduce((sum, v) => sum + v, 0)
      ),
    [
      projectYears,
      amortisedExpenses,
      depositsB,
      sundryDebtorsLocal,
      otherCurrentAssetsByYear,
      loanAndAdvances,
      cashAndBankBalance,
    ]
  );

  // ─── TOTAL (A+B+C) ───────────────────────────────────────────────────────
  const totalABC = useMemo(
    () =>
      projectYears.map((_, i) =>
        Number(
          ((netBlock[i] || 0) + depositsB + (fourthTotals[i] || 0)).toFixed(2)
        )
      ),
    [projectYears, netBlock, depositsB, fourthTotals]
  );

  // ✅ Balance Sheet Match Check (Liabilities total must equal Assets total)
  const mismatchInfo = useMemo(() => {
    const tol = 0.05; // tolerance to avoid tiny rounding issues
    const diffs = projectYears.map((yr, i) => {
      const left = Number((mainTotals[i] || 0).toFixed(2));
      const right = Number((totalABC[i] || 0).toFixed(2));
      const diff = Number((left - right).toFixed(2));
      return { year: yr, left, right, diff, abs: Math.abs(diff) };
    });
    const mismatch = diffs.some((d) => d.abs > tol);
    return { mismatch, diffs };
  }, [projectYears, mainTotals, totalABC]);

  // ✅ Persist mismatch to localStorage + emit event so ReportPage can disable print
  useEffect(() => {
    if (loading || error) return;
    if (!projectId) return;

    const payload = {
      mismatch: !!mismatchInfo.mismatch,
      diffs: mismatchInfo.diffs,
      updatedAt: Date.now(),
    };

    try {
      localStorage.setItem(
        `balanceSheetMismatch:${projectId}`,
        JSON.stringify(payload)
      );
    } catch (e) {
      // ignore storage errors
    }

    window.dispatchEvent(
      new CustomEvent("balanceSheet:mismatch", {
        detail: { projectId, ...payload },
      })
    );

    if (mismatchInfo.mismatch && !dismissedMismatchOnce) {
      setShowMismatchDialog(true);
    }
  }, [loading, error, projectId, mismatchInfo, dismissedMismatchOnce]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-gray-600">
        Loading projected balance sheet…
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-center py-8">{error}</div>;
  }

  return (
    <div className="p-6 space-y-8">
      {/* ✅ Banner when mismatch */}
      {mismatchInfo.mismatch && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-800">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold">
                Balance Sheet mismatch detected
              </div>
              <div className="text-sm">
                Total of Liabilities table is not matching TOTAL (A+B+C).
                Printing will be disabled until this is fixed.
              </div>
            </div>
            <button
              className="text-sm px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
              onClick={() => setShowMismatchDialog(true)}
            >
              View Details
            </button>
          </div>
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-800">
        Projected Balance Sheet
      </h1>

      {/* ── Main Liabilities & Equity ──────────────────────────────────── */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Details</th>
              {projectYears.map((yr, idx) => (
                <th key={idx} className="px-4 py-2 text-center font-semibold">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr>
              <td className="px-4 py-2">Capacity Utilization (%)</td>
              {capacityUtilization.map((u, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {(typeof u === "object" ? u.value : u) ?? 0}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Proprietor’s Capital Account</td>
              {proprietorAccount.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2">Term Loan Balance</td>
              {termLoanBalance.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Working Capital (Bank)</td>
              {workingCapitalBank.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2">
                Sundry Creditors &amp; Other Liabilities
              </td>
              {sundryCreditors.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-200 font-semibold">
              <td className="px-4 py-2 text-right font-bold">Total</td>
              {mainTotals.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center font-bold">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>

            {/* ✅ show diff row (optional but helpful) */}
            {mismatchInfo.mismatch && (
              <tr className="bg-red-50">
                <td className="px-4 py-2 text-right font-semibold text-red-800">
                  Diff (Liabilities - TOTAL A+B+C)
                </td>
                {mismatchInfo.diffs.map((d, i) => (
                  <td
                    key={i}
                    className="px-4 py-2 text-center font-semibold text-red-800"
                  >
                    {Number(d.diff || 0).toFixed(2)}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Balance Sheet Additions ────────────────────────────────────── */}
      <h2 className="text-xl font-semibold text-gray-700">
        Balance Sheet Additions
      </h2>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Details</th>
              {projectYears.map((yr, idx) => (
                <th key={idx} className="px-4 py-2 text-center font-semibold">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr>
              <td className="px-4 py-2">Gross Block</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(depreciationCostTotal || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Depreciation</td>
              {depreciationTotals.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-200 font-semibold">
              <td className="px-4 py-2 text-right font-bold">Net Block</td>
              {netBlock.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center font-bold">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Balance Sheet Amortisations ────────────────────────────────── */}
      <h2 className="text-xl font-semibold text-gray-700">
        Balance Sheet Amortisations
      </h2>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Details</th>
              {projectYears.map((yr, idx) => (
                <th key={idx} className="px-4 py-2 text-center font-semibold">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr>
              <td className="px-4 py-2">Amortised Expenses</td>
              {amortisedExpenses.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Deposits (B)</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(depositsB || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2">Sundry Debtors</td>
              {sundryDebtorsLocal.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Closing Stock</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(
                    (rawMaterialsByYear[i] || 0) + (finishedGoodsByYear[i] || 0)
                  ).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2">Other Current Assets</td>
              {otherCurrentAssetsByYear.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Loans &amp; Advances</td>
              {loanAndAdvances.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-yellow-50 font-semibold">
              <td className="px-4 py-2 text-right font-bold">
                Cash &amp; Bank Balance
              </td>
              {cashAndBankBalance.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center font-bold">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-200 font-semibold">
              <td className="px-4 py-2 text-right font-bold">Total</td>
              {fourthTotals.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center font-bold">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-green-200 font-semibold">
              <td className="px-4 py-2 text-right font-bold">TOTAL (A+B+C)</td>
              {totalABC.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center font-bold">
                  {Number(v || 0).toFixed(2)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ✅ Mismatch Dialog */}
      {showMismatchDialog && mismatchInfo.mismatch && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="font-semibold text-gray-800">
                Balance Sheet Data is not matching
              </div>
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                onClick={() => {
                  setShowMismatchDialog(false);
                  setDismissedMismatchOnce(true);
                }}
              >
                Close
              </button>
            </div>

            <div className="p-4">
              <div className="text-sm text-gray-700 mb-3">
                Your <b>Liabilities Total</b> should match <b>TOTAL (A+B+C)</b>{" "}
                for each year. Differences found:
              </div>

              <div className="overflow-x-auto border rounded">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Year</th>
                      <th className="px-3 py-2 text-right">
                        Liabilities Total
                      </th>
                      <th className="px-3 py-2 text-right">TOTAL (A+B+C)</th>
                      <th className="px-3 py-2 text-right">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mismatchInfo.diffs.map((d, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">{d.year}</td>
                        <td className="px-3 py-2 text-right">
                          {d.left.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {d.right.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-red-700">
                          {d.diff.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 text-xs text-gray-500">
                Note: Printing will remain disabled until the mismatch is
                corrected.
              </div>
            </div>

            <div className="border-t px-4 py-3 flex justify-end">
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 text-sm"
                onClick={() => {
                  setShowMismatchDialog(false);
                  setDismissedMismatchOnce(true);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
